import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { Blueprint } from '@/components/blueprint'
import { Page } from '@/components/page'
import { Segmented } from '@/components/segmented'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { KIND_LABELS, useLabelProfiles } from '@/features/labels/api'
import {
  usePreferences,
  type Density,
  type HomeScreen,
} from '@/features/preferences/preferences-store'
import { parseQuickTake } from '@/features/preferences/quick-take'
import { useSettings } from '@/features/settings/api'
import { useThemeStore, type ThemePreference } from '@/features/theme/theme-store'

export const Route = createFileRoute('/preferences')({ component: PreferencesPage })

const NO_PROFILE = 'none'

/** Setup › Preferences (screen 3e): how this device shows Fundus (D34). */
function PreferencesPage() {
  const prefs = usePreferences()
  const theme = useThemeStore((state) => state.preference)
  const setTheme = useThemeStore((state) => state.setPreference)
  const profiles = useLabelProfiles()
  const settings = useSettings()

  return (
    <Page title="Preferences" description="Kept on this device only.">
      <Blueprint className="flex max-w-xl flex-col gap-5 p-5">
        <Field label="Density" hint="Shelf gives every control a thumb-sized target.">
          <Segmented<Density>
            label="Density"
            value={prefs.density}
            onChange={prefs.setDensity}
            options={[
              { value: 'shelf', label: 'Shelf' },
              { value: 'desk', label: 'Desk' },
              { value: 'auto', label: 'By screen size' },
            ]}
          />
        </Field>

        <Field label="Theme">
          <Segmented<ThemePreference>
            label="Theme"
            value={theme}
            onChange={setTheme}
            options={[
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
              { value: 'system', label: 'System' },
            ]}
          />
        </Field>

        <Field label="Home screen at the desk" htmlFor="pref-home">
          <Select value={prefs.home} onValueChange={(value) => prefs.setHome(value as HomeScreen)}>
            <SelectTrigger id="pref-home">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="overview">Overview</SelectItem>
              <SelectItem value="parts">Parts</SelectItem>
              <SelectItem value="storage">Storage</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <Field
          label="Default label profile"
          htmlFor="pref-label"
          hint="Printing starts from it when it fits the thing being printed."
        >
          <Select
            value={prefs.labelProfileId ?? NO_PROFILE}
            onValueChange={(value) => prefs.setLabelProfileId(value === NO_PROFILE ? null : value)}
          >
            <SelectTrigger id="pref-label">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_PROFILE}>Each kind’s own default</SelectItem>
              {(profiles.data ?? []).map((profile) => (
                <SelectItem key={profile.id} value={profile.id}>
                  {profile.name} ·{' '}
                  {KIND_LABELS[profile.target as keyof typeof KIND_LABELS] ?? profile.target} ·{' '}
                  {profile.width_mm} × {profile.height_mm} mm
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field
          label="Base currency"
          htmlFor="pref-currency"
          hint="Set on the server with BASE_CURRENCY; every price is converted into it."
        >
          <Input id="pref-currency" value={settings.data?.base_currency ?? ''} readOnly />
        </Field>

        <QuickTakeField />
      </Blueprint>
    </Page>
  )
}

function QuickTakeField() {
  const quickTake = usePreferences((state) => state.quickTake)
  const setQuickTake = usePreferences((state) => state.setQuickTake)
  const [text, setText] = useState(quickTake.join(', '))
  const parsed = parseQuickTake(text)

  return (
    <Field
      label="Quick-take amounts on the shelf"
      htmlFor="pref-quick"
      hint="One to four whole numbers, offered when taking stock."
      error={parsed ? undefined : 'One to four whole numbers above zero, like 1, 5, 10.'}
    >
      <Input
        id="pref-quick"
        value={text}
        inputMode="numeric"
        aria-invalid={parsed ? undefined : true}
        onChange={(event) => {
          setText(event.target.value)
          const next = parseQuickTake(event.target.value)
          if (next) setQuickTake(next)
        }}
      />
    </Field>
  )
}

function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string
  htmlFor?: string
  hint?: string
  error?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {htmlFor ? (
        <Label htmlFor={htmlFor} className="text-xs text-neutral-700">
          {label}
        </Label>
      ) : (
        <span className="text-xs text-neutral-700">{label}</span>
      )}
      {children}
      {error ? (
        <p role="alert" className="text-destructive text-xs">
          {error}
        </p>
      ) : (
        hint && <p className="text-xs text-neutral-600">{hint}</p>
      )}
    </div>
  )
}
